import React, { FC, PropsWithChildren } from 'react';
import { Box, StackLayout, UtilityText } from '@leagueplatform/genesis-core';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MenuItem, SelectActionMenuProps } from '../../types/components.types';
import { createMenuItems } from './create-menu-items/create-menu-items';
import { ConfirmationModal } from '../confirmation-modal/confirmation-modal.component';
import { dropdownMenuContentStyles } from './select-action-menu.styles';
import { SelectActionMenuItem } from './select-action-menu-item/select-action-menu-item.component';

/**
 * SelectActionMenu is built on top of the `DropdownMenu` from `@radix-ui/react-dropdown-menu`. It renders a dropdown menu with grouped action items, following the [Menu Button](https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/) and [Menu](https://www.w3.org/WAI/ARIA/apg/patterns/menubar/) semantics and keyboard controls.
 * **Note** that the `menu` and `menuitem` roles used for this component are specifically meant to handle a **"grouping of common actions or functions that the user can invoke"** ([via MDN](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/menu_role)).
 * Think of `SelectActionMenu` as an options popup menu on a section or element within the interface, this is not a replacement for a native <select> form element.
 *
 * This component provides a user-friendly and consistent way to present a list of selectable options with optional groupings that render a semantic separator and visual break between groupings.
 *
 * @param {SelectMenuItemGroup[]} props.menuGroups - An array of menu item groups, each containing an array of `MenuItem` objects.
 * @param {string} [props.precedingLabel] - An optional preceding label for the UI, this is hidden from screen readers as it is not semantically linked to the menu.
 * @param {React.ReactNode} props.children - The content to render as the trigger button, typically a SelectActionMenuButton component.
 *
 * Each `MenuItem` can have an optional `confirmationModal` property. If this property is set, a confirmation modal will be shown
 * before the item's action is triggered. The `confirmationModal` property should be an object with the following properties:
 *   - `title`: The title of the confirmation modal.
 *   - `description`: The description to be displayed in the modal.
 *   - `confirmCta`: The text for the confirm button in the modal.
 *   - `checkboxConfirmationLabel`: (optional) renders a checkbox that must be checked before the confirm button is enabled.
 */

export const SelectActionMenu: FC<PropsWithChildren<SelectActionMenuProps>> = ({
  menuGroups,
  precedingLabel,
  footerContent,
  contentCss,
  children,
}) => {
  const [openItemModal, setOpenItemModal] = React.useState<MenuItem | null>(
    null,
  );
  // used to manage the radix dropdown open state to avoid conflicts with the modal actions- this way we can manage the close of the dropdown before the modal trigger is opened
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const handleModalOpenChange = (item: MenuItem | null) => {
    setOpenItemModal((prevItem) => {
      if (prevItem !== null) {
        return null;
      }
      return item;
    });
  };

  const handleCloseModal = () => {
    setOpenItemModal(null);
  };

  const menuItems = createMenuItems({
    menuGroups,
  });

  return (
    <>
      <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <StackLayout orientation="horizontal" verticalAlignment="center">
          {precedingLabel && (
            <UtilityText css={{ typography: '$label' }} aria-hidden>
              {precedingLabel}
            </UtilityText>
          )}
          <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
        </StackLayout>
        <Box
          as={DropdownMenu.Content}
          side="bottom"
          align="start"
          avoidCollisions
          collisionPadding={10}
          sticky="always"
          sideOffset={5}
          css={{ ...dropdownMenuContentStyles, ...contentCss }}
          data-testid="select-action-menu-content"
        >
          <DropdownMenu.Group>
            {menuItems.map((item) => (
              <SelectActionMenuItem
                key={`menu-item-${item.id}`}
                item={item}
                handleModalOpenChange={handleModalOpenChange}
              />
            ))}
          </DropdownMenu.Group>
          {footerContent && (
            <DropdownMenu.Label asChild>{footerContent}</DropdownMenu.Label>
          )}
        </Box>
      </DropdownMenu.Root>
      {openItemModal && openItemModal.confirmationModal && (
        <ConfirmationModal
          open={Boolean(openItemModal)}
          onOpenChange={handleCloseModal}
          title={openItemModal?.confirmationModal?.title}
          description={openItemModal?.confirmationModal?.description}
          confirmCtaAction={() => {
            openItemModal?.action?.();
            handleCloseModal();
          }}
          confirmCtaText={openItemModal?.confirmationModal?.confirmCta}
          checkboxConfirmationLabel={
            openItemModal?.confirmationModal?.checkboxConfirmationLabel
          }
          destructive={openItemModal?.confirmationModal?.destructive}
        />
      )}
    </>
  );
};
